import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { articles } from '../../../mocks/articles';
import { mockNotes } from '../../../mocks/notes';
import { portfolioWorks } from '../../../mocks/portfolio';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    articles: 0,
    notes: 0,
    portfolio: 0,
    views: 0,
  });

  useEffect(() => {
    // 计算统计数据
    setStats({
      articles: articles.length,
      notes: mockNotes.length,
      portfolio: portfolioWorks.length,
      views: 15847, // 模拟访问量
    });
  }, []);

  // 获取最新文章（前5条）
  const recentArticles = [...articles]
    .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
    .slice(0, 5);

  // 获取最新笔记（前5条）
  const recentNotes = [...mockNotes]
    .slice(0, 5);

  return (
    <div className="p-8">
      {/* 欢迎信息 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          欢迎回来，{user?.username || '管理员'}！
        </h1>
        <p className="text-gray-600">这是您的内容管理概览</p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-article-line text-2xl"></i>
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
              文章
            </span>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.articles}</div>
          <div className="text-blue-100 text-sm">已发布文章</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-sticky-note-line text-2xl"></i>
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
              笔记
            </span>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.notes}</div>
          <div className="text-purple-100 text-sm">学习笔记</div>
        </div>

        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-image-line text-2xl"></i>
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
              作品
            </span>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.portfolio}</div>
          <div className="text-teal-100 text-sm">作品集项目</div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-eye-line text-2xl"></i>
            </div>
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
              访问
            </span>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.views.toLocaleString()}</div>
          <div className="text-orange-100 text-sm">总访问量</div>
        </div>
      </div>

      {/* 最近内容 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 最新文章 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">最新文章</h2>
              <button className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap">
                查看全部 →
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {recentArticles.map((article) => (
              <div key={article.id} className="p-4 hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="flex items-start gap-3">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-800 mb-1 line-clamp-1">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        {article.author.name}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.publishDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line"></i>
                        {article.likes}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 最新笔记 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">最新笔记</h2>
              <button className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap">
                查看全部 →
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {recentNotes.map((note) => (
              <div key={note.id} className="p-4 hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="flex items-start gap-3">
                  {note.image && (
                    <img
                      src={note.image}
                      alt={note.title}
                      className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                  )}
                  {!note.image && (
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-sticky-note-line text-2xl text-purple-600"></i>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-800 line-clamp-1 flex-1">
                        {note.title}
                      </h3>
                      {note.isPinned && (
                        <i className="ri-pushpin-fill text-orange-500 text-sm flex-shrink-0"></i>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        {note.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {note.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line"></i>
                        {note.likes}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 快捷操作 */}
      <div className="mt-6 bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">快捷操作</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="bg-white hover:bg-gray-50 rounded-lg p-4 text-center transition-all shadow-sm border border-gray-100 hover:shadow-md">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <i className="ri-add-line text-2xl text-blue-600"></i>
            </div>
            <div className="text-sm font-medium text-gray-700 whitespace-nowrap">新建文章</div>
          </button>
          <button className="bg-white hover:bg-gray-50 rounded-lg p-4 text-center transition-all shadow-sm border border-gray-100 hover:shadow-md">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <i className="ri-add-line text-2xl text-purple-600"></i>
            </div>
            <div className="text-sm font-medium text-gray-700 whitespace-nowrap">新建笔记</div>
          </button>
          <button className="bg-white hover:bg-gray-50 rounded-lg p-4 text-center transition-all shadow-sm border border-gray-100 hover:shadow-md">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <i className="ri-add-line text-2xl text-teal-600"></i>
            </div>
            <div className="text-sm font-medium text-gray-700 whitespace-nowrap">上传作品</div>
          </button>
          <button className="bg-white hover:bg-gray-50 rounded-lg p-4 text-center transition-all shadow-sm border border-gray-100 hover:shadow-md">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <i className="ri-settings-3-line text-2xl text-orange-600"></i>
            </div>
            <div className="text-sm font-medium text-gray-700 whitespace-nowrap">网站设置</div>
          </button>
        </div>
      </div>
    </div>
  );
}